import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  Download,
  FileText,
  GraduationCap,
  RefreshCw,
  Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTeacherData } from '../../hooks/useTeacherData';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import advancedReportsService, {
  type AssignmentAnalytics,
} from '../../services/advancedReportsService';
import subscriptionService from '../../services/subscriptionService';
import { hasFeature } from '../../types/subscription';
import Button from '../ui/Button';
import DataTable from '../ui/DataTable';
import EmptyState from '../ui/EmptyState';
import MetricCard from '../ui/MetricCard';
import PageHeader from '../ui/PageHeader';
import StatusBadge from '../ui/StatusBadge';
import Surface from '../ui/Surface';
import type { DataTableColumn } from '../../types/ui';
import type { Class, Payment, Student } from '../../types/firestore';

type ReportView = 'summary' | 'students' | 'classes' | 'payments' | 'assignments';

const viewLabels: Record<ReportView, string> = {
  summary: 'الملخص',
  students: 'الطلاب',
  classes: 'الصفوف',
  payments: 'المدفوعات',
  assignments: 'الواجبات',
};

const toDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (date: Date | null) =>
  date
    ? date.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'غير محدد';

const TeacherReports: React.FC = () => {
  const {
    stats,
    students,
    classes,
    recentPayments,
    loading,
    error,
    refreshData,
  } = useTeacherData();
  const { user } = useAuth();
  const { formatAmount } = useCurrency();
  const [activeView, setActiveView] = useState<ReportView>('summary');
  const [assignmentAnalytics, setAssignmentAnalytics] = useState<
    AssignmentAnalytics[]
  >([]);
  const [assignmentReportsAllowed, setAssignmentReportsAllowed] =
    useState(false);
  const [assignmentExportAllowed, setAssignmentExportAllowed] = useState(false);
  const [assignmentReportLoading, setAssignmentReportLoading] = useState(false);

  const visibleReportViews = useMemo(
    () =>
      (Object.keys(viewLabels) as ReportView[]).filter(
        view => view !== 'assignments' || assignmentReportsAllowed
      ),
    [assignmentReportsAllowed]
  );

  useEffect(() => {
    if (activeView === 'assignments' && !assignmentReportsAllowed) {
      setActiveView('summary');
    }
  }, [activeView, assignmentReportsAllowed]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const loadAssignmentReports = async () => {
      setAssignmentReportLoading(true);
      try {
        const subscription = await subscriptionService.getSubscription(user.id);
        const canView = subscription
          ? hasFeature(subscription.planId, 'advancedAssignmentReports')
          : false;
        const canExport = subscription
          ? hasFeature(subscription.planId, 'assignmentExcelExport')
          : false;

        if (cancelled) return;
        setAssignmentReportsAllowed(canView);
        setAssignmentExportAllowed(canExport);
        if (!canView) {
          setAssignmentAnalytics([]);
          return;
        }

        const analytics = await advancedReportsService.getAssignmentAnalytics(
          user.id
        );
        if (!cancelled) setAssignmentAnalytics(analytics);
      } catch (reportError) {
        console.error('Error loading assignment reports:', reportError);
        if (!cancelled) {
          setAssignmentReportsAllowed(false);
          setAssignmentAnalytics([]);
        }
      } finally {
        if (!cancelled) setAssignmentReportLoading(false);
      }
    };

    void loadAssignmentReports();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const activeStudents = useMemo(
    () => students.filter(student => student.isActive),
    [students]
  );

  const studentsWithGrades = useMemo(
    () =>
      students
        .filter(student => typeof student.averageGrade === 'number')
        .sort(
          (first, second) =>
            (second.averageGrade || 0) - (first.averageGrade || 0)
        ),
    [students]
  );

  const paymentSummary = useMemo(() => {
    const paid = recentPayments.filter(payment => payment.status === 'paid');
    const pending = recentPayments.filter(payment => payment.status === 'pending');
    const overdue = recentPayments.filter(payment => payment.status === 'overdue');

    return {
      paidCount: paid.length,
      pendingCount: pending.length,
      overdueCount: overdue.length,
      paidAmount: paid.reduce((sum, payment) => sum + (payment.amount || 0), 0),
      pendingAmount: pending.reduce(
        (sum, payment) => sum + (payment.amount || 0),
        0
      ),
    };
  }, [recentPayments]);

  const classSummary = useMemo(() => {
    const active = classes.filter(classItem => classItem.isActive);
    const planned = classes.reduce(
      (sum, classItem) => sum + (classItem.totalSessions || 0),
      0
    );
    const completed = classes.reduce(
      (sum, classItem) => sum + (classItem.completedSessions || 0),
      0
    );

    return {
      activeCount: active.length,
      planned,
      completed,
      completionRate:
        planned > 0 ? Math.min(100, Math.round((completed / planned) * 100)) : 0,
    };
  }, [classes]);

  const assignmentSummary = useMemo(() => {
    const targets = assignmentAnalytics.reduce(
      (sum, item) => sum + item.targetCount,
      0
    );
    const submitted = assignmentAnalytics.reduce(
      (sum, item) => sum + item.submittedCount,
      0
    );
    const late = assignmentAnalytics.reduce(
      (sum, item) => sum + item.lateCount,
      0
    );
    const graded = assignmentAnalytics.reduce(
      (sum, item) => sum + item.gradedCount,
      0
    );
    const scored = assignmentAnalytics.filter(
      item => item.averageScorePercent > 0
    );

    return {
      targets,
      submitted,
      late,
      graded,
      submissionRate:
        targets > 0 ? Math.round((submitted / targets) * 100) : 0,
      averageScore:
        scored.length > 0
          ? Math.round(
              scored.reduce(
                (sum, item) => sum + item.averageScorePercent,
                0
              ) / scored.length
            )
          : 0,
    };
  }, [assignmentAnalytics]);

  const classAssignmentComparison = useMemo(() => {
    const grouped = new Map<
      string,
      { className: string; targetCount: number; submittedCount: number; lateCount: number }
    >();

    assignmentAnalytics.forEach(item => {
      const current = grouped.get(item.classId) || {
        className: item.className,
        targetCount: 0,
        submittedCount: 0,
        lateCount: 0,
      };
      current.targetCount += item.targetCount;
      current.submittedCount += item.submittedCount;
      current.lateCount += item.lateCount;
      grouped.set(item.classId, current);
    });

    return Array.from(grouped.entries()).map(([classId, item]) => ({
      classId,
      ...item,
      submissionRate:
        item.targetCount > 0
          ? Math.round((item.submittedCount / item.targetCount) * 100)
          : 0,
    }));
  }, [assignmentAnalytics]);

  const studentColumns: DataTableColumn<Student>[] = [
    {
      key: 'name',
      header: 'الطالب',
      cell: student => (
        <div>
          <p className='font-semibold text-secondary-950 dark:text-white'>
            {student.name}
          </p>
          <p className='mt-1 text-xs text-secondary-500 dark:text-secondary-400'>
            {student.email || student.phone || 'لا توجد بيانات تواصل'}
          </p>
        </div>
      ),
    },
    {
      key: 'classes',
      header: 'الفصول',
      hideOnMobile: true,
      cell: student => student.enrolledClasses?.length || 0,
    },
    {
      key: 'grade',
      header: 'المعدل',
      cell: student =>
        typeof student.averageGrade === 'number'
          ? student.averageGrade.toFixed(1)
          : 'غير محسوب',
    },
    {
      key: 'status',
      header: 'الحالة',
      cell: student => (
        <StatusBadge tone={student.isActive ? 'success' : 'neutral'}>
          {student.isActive ? 'نشط' : 'غير نشط'}
        </StatusBadge>
      ),
    },
  ];

  const classColumns: DataTableColumn<Class>[] = [
    {
      key: 'subject',
      header: 'الصف',
      cell: classItem => (
        <div>
          <p className='font-semibold text-secondary-950 dark:text-white'>
            {classItem.subject}
          </p>
          <p className='mt-1 text-xs text-secondary-500 dark:text-secondary-400'>
            {classItem.grade || 'مرحلة غير محددة'}
          </p>
        </div>
      ),
    },
    {
      key: 'students',
      header: 'الطلاب',
      cell: classItem => classItem.enrolledStudents?.length || 0,
    },
    {
      key: 'sessions',
      header: 'الحصص',
      hideOnMobile: true,
      cell: classItem =>
        `${classItem.completedSessions || 0}/${classItem.totalSessions || 0}`,
    },
    {
      key: 'status',
      header: 'الحالة',
      cell: classItem => (
        <StatusBadge tone={classItem.isActive ? 'success' : 'neutral'}>
          {classItem.isActive ? 'نشط' : 'متوقف'}
        </StatusBadge>
      ),
    },
  ];

  const paymentColumns: DataTableColumn<Payment>[] = [
    {
      key: 'student',
      header: 'الطالب',
      cell: payment =>
        (payment as any).studentName || payment.studentId || 'طالب',
    },
    {
      key: 'amount',
      header: 'المبلغ',
      cell: payment => formatAmount(payment.amount || 0),
    },
    {
      key: 'date',
      header: 'التاريخ',
      hideOnMobile: true,
      cell: payment => formatDate(toDate(payment.createdAt || payment.dueDate)),
    },
    {
      key: 'status',
      header: 'الحالة',
      cell: payment => (
        <StatusBadge
          tone={
            payment.status === 'paid'
              ? 'success'
              : payment.status === 'overdue'
                ? 'danger'
                : 'warning'
          }
        >
          {payment.status === 'paid'
            ? 'مدفوع'
            : payment.status === 'overdue'
              ? 'متأخر'
              : 'في الانتظار'}
        </StatusBadge>
      ),
    },
  ];

  const assignmentColumns: DataTableColumn<AssignmentAnalytics>[] = [
    {
      key: 'title',
      header: 'الواجب',
      cell: item => (
        <div>
          <p className='font-semibold text-secondary-950 dark:text-white'>
            {item.title}
          </p>
          <p className='mt-1 text-xs text-secondary-500 dark:text-secondary-400'>
            {item.className} - {item.subject || 'مادة غير محددة'}
          </p>
        </div>
      ),
    },
    {
      key: 'submissionRate',
      header: 'معدل التسليم',
      cell: item => `${item.submissionRate}%`,
    },
    {
      key: 'late',
      header: 'المتأخرون',
      hideOnMobile: true,
      cell: item => item.lateCount,
    },
    {
      key: 'average',
      header: 'متوسط الدرجة',
      cell: item =>
        item.gradedCount > 0
          ? `${item.averageScorePercent.toFixed(1)}%`
          : 'غير محسوب',
    },
    {
      key: 'status',
      header: 'الحالة',
      cell: item => (
        <StatusBadge
          tone={
            item.missingCount > 0
              ? 'warning'
              : item.gradedCount >= item.submittedCount
                ? 'success'
                : 'info'
          }
        >
          {item.missingCount > 0
            ? `${item.missingCount} لم يسلموا`
            : item.gradedCount >= item.submittedCount
              ? 'مكتمل'
              : 'قيد التصحيح'}
        </StatusBadge>
      ),
    },
  ];

  const handleExportAssignmentReport = () => {
    if (!assignmentExportAllowed) {
      toast.error('تصدير واجبات Excel غير متاح في خطتك الحالية');
      return;
    }
    const csv = advancedReportsService.exportAssignmentAnalyticsToCSV(
      assignmentAnalytics
    );
    const blob = new Blob([`\uFEFF${csv}`], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `assignment-analytics-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Surface className='flex min-h-[24rem] items-center justify-center'>
        <div className='text-center'>
          <div className='mx-auto h-10 w-10 animate-spin rounded-full border-2 border-primary-600 border-l-transparent' />
          <p className='mt-3 text-sm text-secondary-500 dark:text-secondary-400'>
            جار تحميل تقارير المدرس...
          </p>
        </div>
      </Surface>
    );
  }

  if (error) {
    return (
      <Surface>
        <EmptyState
          icon={RefreshCw}
          title='تعذر تحميل التقارير'
          description={error}
          action={
            <Button
              onClick={refreshData}
              leadingIcon={<RefreshCw className='h-4 w-4' />}
            >
              إعادة المحاولة
            </Button>
          }
        />
      </Surface>
    );
  }

  return (
    <div className='space-y-6'>
      <PageHeader
        eyebrow='تقارير فعلية'
        title='تقارير المدرس'
        description='ملخصات مستندة إلى بيانات الطلاب والصفوف والمدفوعات الحالية دون أرقام تجريبية.'
        actions={
          <div className='flex flex-wrap gap-2'>
            {activeView === 'assignments' ? (
              <Button
                variant='secondary'
                onClick={handleExportAssignmentReport}
                leadingIcon={<Download className='h-4 w-4' />}
                disabled={
                  !assignmentExportAllowed || assignmentAnalytics.length === 0
                }
                title={
                  assignmentExportAllowed
                    ? undefined
                    : 'تصدير واجبات Excel يحتاج خطة مناسبة'
                }
              >
                تصدير Excel
              </Button>
            ) : null}
            <Button
              variant='secondary'
              onClick={refreshData}
              leadingIcon={<RefreshCw className='h-4 w-4' />}
            >
              تحديث
            </Button>
          </div>
        }
      />

      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4'>
        <MetricCard
          label='إجمالي الطلاب'
          value={stats?.totalStudents || 0}
          icon={Users}
          note={`${activeStudents.length} طالب نشط`}
          tone='blue'
        />
        <MetricCard
          label='الصفوف النشطة'
          value={classSummary.activeCount}
          icon={GraduationCap}
          note={`${classes.length} صف إجمالي`}
          tone='green'
        />
        <MetricCard
          label='إيرادات الشهر'
          value={formatAmount(stats?.monthlyRevenue || 0)}
          icon={CircleDollarSign}
          note={`${paymentSummary.paidCount} دفعة مدفوعة`}
          tone='amber'
        />
        <MetricCard
          label='إنجاز الحصص'
          value={`${classSummary.completionRate}%`}
          icon={CheckCircle2}
          note={`${classSummary.completed}/${classSummary.planned} حصة`}
          tone='red'
        />
      </div>

      <Surface>
        <div className='flex flex-wrap gap-2'>
          {visibleReportViews.map(view => (
            <Button
              key={view}
              size='sm'
              variant={activeView === view ? 'primary' : 'secondary'}
              onClick={() => setActiveView(view)}
            >
              {viewLabels[view]}
            </Button>
          ))}
        </div>
      </Surface>

      {activeView === 'summary' ? (
        <div className='grid grid-cols-1 gap-6 xl:grid-cols-2'>
          <Surface>
            <div className='mb-4 flex items-center gap-2'>
              <BarChart3 className='h-5 w-5 text-primary-600' />
              <h2 className='font-bold text-secondary-950 dark:text-white'>
                ملخص الصفوف
              </h2>
            </div>
            <div className='space-y-4'>
              <div className='flex items-center justify-between text-sm'>
                <span className='text-secondary-500 dark:text-secondary-400'>
                  الحصص المكتملة
                </span>
                <span className='font-semibold text-secondary-950 dark:text-white'>
                  {classSummary.completed} من {classSummary.planned}
                </span>
              </div>
              <div className='h-2 overflow-hidden rounded-full bg-secondary-100 dark:bg-secondary-800'>
                <div
                  className='h-full rounded-full bg-primary-600'
                  style={{ width: `${classSummary.completionRate}%` }}
                />
              </div>
              <p className='text-sm text-secondary-500 dark:text-secondary-400'>
                يعتمد هذا المؤشر على `totalSessions` و`completedSessions` داخل
                بيانات الصفوف.
              </p>
            </div>
          </Surface>

          <Surface>
            <div className='mb-4 flex items-center gap-2'>
              <CalendarDays className='h-5 w-5 text-primary-600' />
              <h2 className='font-bold text-secondary-950 dark:text-white'>
                ملخص المدفوعات
              </h2>
            </div>
            <div className='grid gap-3 sm:grid-cols-3'>
              <div className='rounded-xl bg-success-50 p-4 text-success-700 dark:bg-success-950 dark:text-success-300'>
                <p className='text-xs font-semibold'>مدفوعة</p>
                <p className='mt-2 text-2xl font-bold'>{paymentSummary.paidCount}</p>
              </div>
              <div className='rounded-xl bg-warning-50 p-4 text-warning-700 dark:bg-warning-950 dark:text-warning-300'>
                <p className='text-xs font-semibold'>معلقة</p>
                <p className='mt-2 text-2xl font-bold'>
                  {paymentSummary.pendingCount}
                </p>
              </div>
              <div className='rounded-xl bg-danger-50 p-4 text-danger-700 dark:bg-danger-950 dark:text-danger-300'>
                <p className='text-xs font-semibold'>متأخرة</p>
                <p className='mt-2 text-2xl font-bold'>
                  {paymentSummary.overdueCount}
                </p>
              </div>
            </div>
            <p className='mt-4 text-sm text-secondary-500 dark:text-secondary-400'>
              إجمالي قيد التحصيل: {formatAmount(paymentSummary.pendingAmount)}
            </p>
          </Surface>
        </div>
      ) : null}

      {activeView === 'students' ? (
        <Surface padding='none' className='overflow-hidden'>
          <DataTable
            caption='تقرير الطلاب'
            columns={studentColumns}
            rows={studentsWithGrades.length > 0 ? studentsWithGrades : students}
            rowKey={student => student.id || student.email || student.name}
            emptyState={
              <EmptyState
                icon={Users}
                title='لا توجد بيانات طلاب'
                description='ستظهر تقارير الطلاب بعد إضافة الطلاب أو اعتماد طلبات الربط.'
              />
            }
          />
        </Surface>
      ) : null}

      {activeView === 'classes' ? (
        <Surface padding='none' className='overflow-hidden'>
          <DataTable
            caption='تقرير الصفوف'
            columns={classColumns}
            rows={classes}
            rowKey={classItem => classItem.id || classItem.subject}
            emptyState={
              <EmptyState
                icon={GraduationCap}
                title='لا توجد صفوف'
                description='أنشئ صفًا أو حصة حتى تظهر بيانات الأداء هنا.'
              />
            }
          />
        </Surface>
      ) : null}

      {activeView === 'payments' ? (
        <Surface padding='none' className='overflow-hidden'>
          <DataTable
            caption='تقرير المدفوعات'
            columns={paymentColumns}
            rows={recentPayments}
            rowKey={payment => payment.id || `${payment.studentId}-${payment.amount}`}
            emptyState={
              <EmptyState
                icon={FileText}
                title='لا توجد مدفوعات هذا الشهر'
                description='ستظهر المدفوعات الحديثة بعد تسجيلها من دفتر المدفوعات.'
              />
            }
          />
        </Surface>
      ) : null}

      {activeView === 'assignments' ? (
        <div className='space-y-6'>
          {assignmentReportLoading ? (
            <Surface className='flex min-h-[12rem] items-center justify-center'>
              <div className='text-center'>
                <div className='mx-auto h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-l-transparent' />
                <p className='mt-3 text-sm text-secondary-500 dark:text-secondary-400'>
                  جار تحميل تحليلات الواجبات...
                </p>
              </div>
            </Surface>
          ) : (
            <>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4'>
                <MetricCard
                  label='معدل التسليم'
                  value={`${assignmentSummary.submissionRate}%`}
                  icon={CheckCircle2}
                  note={`${assignmentSummary.submitted}/${assignmentSummary.targets} طالب`}
                  tone='green'
                />
                <MetricCard
                  label='متوسط الدرجات'
                  value={`${assignmentSummary.averageScore}%`}
                  icon={BarChart3}
                  note={`${assignmentSummary.graded} تسليم مقيم`}
                  tone='blue'
                />
                <MetricCard
                  label='تسليمات متأخرة'
                  value={assignmentSummary.late}
                  icon={CalendarDays}
                  tone='red'
                />
                <MetricCard
                  label='عدد الواجبات'
                  value={assignmentAnalytics.length}
                  icon={FileText}
                  tone='amber'
                />
              </div>

              <div className='grid grid-cols-1 gap-6 xl:grid-cols-2'>
                <Surface>
                  <div className='mb-4 flex items-center gap-2'>
                    <GraduationCap className='h-5 w-5 text-primary-600' />
                    <h2 className='font-bold text-secondary-950 dark:text-white'>
                      مقارنة الفصول
                    </h2>
                  </div>
                  {classAssignmentComparison.length ? (
                    <div className='space-y-4'>
                      {classAssignmentComparison.map(item => (
                        <div key={item.classId || item.className}>
                          <div className='mb-1 flex items-center justify-between text-sm'>
                            <span className='font-semibold text-secondary-800 dark:text-secondary-100'>
                              {item.className}
                            </span>
                            <span className='text-secondary-500'>
                              {item.submissionRate}%
                            </span>
                          </div>
                          <div className='h-2 overflow-hidden rounded-full bg-secondary-100 dark:bg-secondary-800'>
                            <div
                              className='h-full rounded-full bg-primary-600'
                              style={{ width: `${item.submissionRate}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className='text-sm text-secondary-500 dark:text-secondary-400'>
                      لا توجد بيانات واجبات كافية للمقارنة بعد.
                    </p>
                  )}
                </Surface>

                <Surface>
                  <div className='mb-4 flex items-center gap-2'>
                    <Users className='h-5 w-5 text-primary-600' />
                    <h2 className='font-bold text-secondary-950 dark:text-white'>
                      أعلى واجبات في التسليم
                    </h2>
                  </div>
                  {assignmentAnalytics.length ? (
                    <div className='space-y-3'>
                      {[...assignmentAnalytics]
                        .sort(
                          (first, second) =>
                            second.submissionRate - first.submissionRate
                        )
                        .slice(0, 5)
                        .map(item => (
                          <div
                            key={item.assignmentId}
                            className='flex items-center justify-between gap-3 rounded-lg bg-secondary-50 px-3 py-2 dark:bg-secondary-900'
                          >
                            <span className='min-w-0 truncate text-sm font-medium text-secondary-800 dark:text-secondary-100'>
                              {item.title}
                            </span>
                            <StatusBadge tone='success'>
                              {item.submissionRate}%
                            </StatusBadge>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <p className='text-sm text-secondary-500 dark:text-secondary-400'>
                      ستظهر القائمة بعد إنشاء واجبات وتسليم الطلاب.
                    </p>
                  )}
                </Surface>
              </div>

              <Surface padding='none' className='overflow-hidden'>
                <DataTable
                  caption='تحليلات الواجبات'
                  columns={assignmentColumns}
                  rows={assignmentAnalytics}
                  rowKey={item => item.assignmentId}
                  emptyState={
                    <EmptyState
                      icon={FileText}
                      title='لا توجد تحليلات واجبات'
                      description='أنشئ واجبات مرتبطة بطلاب حتى تظهر مؤشرات التسليم والدرجات.'
                    />
                  }
                />
              </Surface>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default TeacherReports;
